// =============================================================================
// iTravel 2.0 — Trips API Service
// =============================================================================
// Typed API functions for all trip-related endpoints:
//
//   TRIPS
//     POST   /trips              — Create a new trip
//     GET    /trips              — List trips (paginated, filtered)
//     GET    /trips/:tripId      — Get trip detail
//     PATCH  /trips/:tripId      — Update trip
//     DELETE /trips/:tripId      — Delete trip
//
//   MEMBERS
//     POST   /trips/:tripId/members              — Invite a member
//     PATCH  /trips/:tripId/members/:userId       — Update member role
//     DELETE /trips/:tripId/members/:userId       — Remove member / leave
//
//   DAYS
//     POST   /trips/:tripId/days              — Create a trip day
//     PATCH  /trips/:tripId/days/:dayId       — Update a trip day
//     DELETE /trips/:tripId/days/:dayId       — Delete a trip day
//     PUT    /trips/:tripId/days/reorder      — Reorder trip days
//
//   TRAVEL POINTS
//     POST   /trips/:tripId/days/:dayId/points              — Create point
//     PATCH  /trips/:tripId/days/:dayId/points/:pointId     — Update point
//     DELETE /trips/:tripId/days/:dayId/points/:pointId     — Delete point
//     PUT    /trips/:tripId/days/:dayId/points/reorder      — Reorder points
//
//   CHECKLIST
//     POST   /trips/:tripId/checklist              — Create checklist item
//     PATCH  /trips/:tripId/checklist/:itemId      — Update checklist item
//     DELETE /trips/:tripId/checklist/:itemId      — Delete checklist item
//     PUT    /trips/:tripId/checklist/reorder      — Reorder checklist items
// =============================================================================

import type {
  CreateTravelPointPayload,
  CreateTripDayPayload,
  CreateTripPayload,
  CurrentTrip,
  InviteTripMemberPayload,
  ReorderItemsPayload,
  TravelPointItemType,
  TripListParams,
  TripMemberRole,
  UpdateTravelPointPayload,
  UpdateTripDayPayload,
  UpdateTripMemberPayload,
  UpdateTripPayload,
} from '@itravel/shared';

import { buildSearchParams } from '@/lib/api/client';
import { api } from '@/lib/api/instance';

// ---------------------------------------------------------------------------
// Response envelope types (match server response format)
// ---------------------------------------------------------------------------

interface ApiDataResponse<T> {
  data: T;
}

interface MessageResponse {
  message: string;
}

// ---------------------------------------------------------------------------
// Response types (from server DTOs)
// ---------------------------------------------------------------------------

export interface UserSummary {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  /** True for participants added without an app account (see guest invites). */
  isGuest: boolean;
  /** An account its owner deleted; only the name is left, in shared trips. */
  isDeleted?: boolean;
}

export interface TripMemberResponse {
  id: string;
  tripId: string;
  userId: string;
  role: TripMemberRole;
  joinedAt: string;
  invitedById: string | null;
  user: UserSummary;
  /** PENDING is an invitation still waiting on an answer — not a member yet. */
  status: 'ACCEPTED' | 'PENDING';
}

/**
 * The envelope, for someone who has not opened it yet. Not a trip — an invited
 * person is not a member, so nothing of the itinerary is theirs to read.
 */
export interface TripInvitationResponse {
  id: string;
  tripId: string;
  tripName: string;
  tripDescription: string | null;
  coverImageUrl: string | null;
  /** Both null for a trip without dates. */
  startDate: string | null;
  endDate: string | null;
  memberCount: number;
  role: TripMemberRole;
  invitedBy: UserSummary;
  createdAt: string;
}

export interface TravelPointResponse {
  id: string;
  tripId: string;
  tripDayId: string | null;
  name: string;
  description: string | null;
  itemType: TravelPointItemType;
  /** Fills the whole day — applies to places and activities alike. */
  isAllDay: boolean;
  /** Null for activities that are not tied to a location. */
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  category: string;
  status: string;
  orderIndex: number;
  placeExternalId: string | null;
  /** The canonical Place this entry visits; null until resolved. */
  placeId: string | null;
  estimatedDurationMinutes: number | null;
  cost: number | null;
  costCurrency: string | null;
  websiteUrl: string | null;
  phoneNumber: string | null;
  imageUrl: string | null;
  /** A photo for the plan: trip photos first, then imageUrl, then an Inspiration. Trip detail only. */
  thumbnailUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A travel point that can actually be drawn on a map.
 *
 * ALL_DAY and TRANSPORT entries have no coordinates, so anything plotting pins
 * or computing bounds must filter with `hasCoordinates` first rather than
 * assuming latitude/longitude are present.
 */
export type MappableTravelPoint = TravelPointResponse & {
  latitude: number;
  longitude: number;
};

export function hasCoordinates(point: TravelPointResponse): point is MappableTravelPoint {
  return point.latitude !== null && point.longitude !== null;
}

export interface TripDayResponse {
  id: string;
  tripId: string;
  /** Null in a trip without dates: the day is then "Day N" by its order. */
  date: string | null;
  title: string | null;
  notes: string | null;
  orderIndex: number;
  points: TravelPointResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistItemResponse {
  id: string;
  tripId: string;
  listId: string;
  text: string;
  isChecked: boolean;
  assignedToId: string | null;
  category: string;
  orderIndex: number;
  assignedTo: UserSummary | null;
  createdAt: string;
  updatedAt: string;
}

export interface TripResponse {
  id: string;
  name: string;
  description: string | null;
  coverImageUrl: string | null;
  /** Both null in an Unscheduled trip (ADR-0008). */
  startDate: string | null;
  endDate: string | null;
  status: string;
  visibility: string;
  defaultCurrency: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  memberCount: number;
  dayCount: number;
  myRole: string;
}

export interface TripDetailResponse extends TripResponse {
  /** Where a trip without dates used to start; its stays are read from it. */
  lastStartDate?: string | null;
  /**
   * False for someone outside a PUBLIC trip: the plan and its organiser only.
   * Missing on a detail cached by an older app, which was always a member's.
   */
  viewerIsMember?: boolean;
  members: TripMemberResponse[];
  days: TripDayResponse[];
  checklist: ChecklistItemResponse[];
  /**
   * Unassigned travel points: on the trip, on none of its days. The wire name
   * predates the term and stays while 1.11 apps still read it.
   */
  unscheduledPoints: TravelPointResponse[];
  // Optional: a trip detail cached offline by an app from before 1.10 has neither.
  /** By check-in date. */
  lodgings?: LodgingResponse[];
  /** "Important info": pinned first. */
  notes?: TripNoteResponse[];
}

/** Where the travellers sleep. The nights run from checkInDate up to, not including, checkOutDate. */
export interface LodgingResponse {
  id: string;
  tripId: string;
  name: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  /** YYYY-MM-DD */
  checkInDate: string;
  checkOutDate: string;
  /** HH:mm */
  checkInTime: string | null;
  checkOutTime: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LodgingPayload {
  name: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  checkInDate: string;
  checkOutDate: string;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  notes?: string | null;
}

export interface TripNoteResponse {
  id: string;
  tripId: string;
  title: string;
  body: string | null;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TripNotePayload {
  title: string;
  body?: string | null;
  pinned?: boolean;
}

export interface TripListResponse {
  trips: TripResponse[];
  meta: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

// ===========================================================================
// TRIP CRUD
// ===========================================================================

/**
 * Create a new trip. The authenticated user becomes the OWNER.
 * Trip days are auto-generated for the date range.
 */
export async function createTrip(payload: CreateTripPayload): Promise<TripDetailResponse> {
  const response = await api
    .post('trips', { json: payload })
    .json<ApiDataResponse<TripDetailResponse>>();

  return response.data;
}

/**
 * Get a paginated list of trips the current user is a member of.
 */
export async function getTrips(params?: TripListParams): Promise<TripListResponse> {
  const searchParams = params
    ? buildSearchParams({
        status: params.status,
        visibility: params.visibility,
        search: params.search,
        sort: params.sort,
        page: params.page,
        perPage: params.perPage,
      })
    : undefined;

  const response = await api
    .get('trips', { searchParams })
    .json<ApiDataResponse<TripListResponse>>();

  return response.data;
}

/**
 * Get full trip details including members, days, points, and checklist.
 */
export async function getTripById(tripId: string): Promise<TripDetailResponse> {
  const response = await api.get(`trips/${tripId}`).json<ApiDataResponse<TripDetailResponse>>();

  return response.data;
}

/**
 * Update trip details (name, description, dates, status, visibility, etc.).
 * Requires ADMIN or OWNER role.
 */
export async function updateTrip(
  tripId: string,
  payload: UpdateTripPayload,
): Promise<TripDetailResponse> {
  const response = await api
    .patch(`trips/${tripId}`, { json: payload })
    .json<ApiDataResponse<TripDetailResponse>>();

  return response.data;
}

/**
 * Delete a trip and all associated data. Only the OWNER can delete.
 */
export async function deleteTrip(tripId: string): Promise<string> {
  const response = await api.delete(`trips/${tripId}`).json<ApiDataResponse<MessageResponse>>();

  return response.data.message;
}

// ===========================================================================
// TRIP MEMBERS
// ===========================================================================

/**
 * Invite a user to the trip. Requires ADMIN or OWNER role.
 */
export async function inviteMember(
  tripId: string,
  payload: InviteTripMemberPayload,
): Promise<TripMemberResponse> {
  const response = await api
    .post(`trips/${tripId}/members`, { json: payload })
    .json<ApiDataResponse<TripMemberResponse>>();

  return response.data;
}

/**
 * What is waiting on a trip you cannot open yet.
 *
 * Every other trip endpoint 404s a non-member, so this is the only thing a
 * tapped invite notification can load. Returns null when there is no invitation
 * — which is the ordinary answer once it has been accepted or declined, and not
 * worth an error path of its own.
 */
export async function getTripInvitation(tripId: string): Promise<TripInvitationResponse | null> {
  try {
    const response = await api
      .get(`trips/${tripId}/invitation`)
      .json<ApiDataResponse<TripInvitationResponse>>();

    return response.data;
  } catch (error) {
    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      (error as { response?: Response }).response?.status === 404
    ) {
      return null;
    }
    throw error;
  }
}

/** Every invitation still waiting on the current user, newest first. */
export async function getTripInvitations(): Promise<TripInvitationResponse[]> {
  const response = await api
    .get('trips/invitations')
    .json<ApiDataResponse<TripInvitationResponse[]>>();
  return response.data;
}

/**
 * Accepts an invitation — the moment you become a member of the trip.
 */
export async function acceptTripInvitation(tripId: string): Promise<TripMemberResponse> {
  const response = await api
    .post(`trips/${tripId}/invitation/accept`)
    .json<ApiDataResponse<TripMemberResponse>>();

  return response.data;
}

/**
 * Declines an invitation, which deletes it. Nothing records the refusal.
 */
export async function declineTripInvitation(tripId: string): Promise<void> {
  await api.delete(`trips/${tripId}/invitation`);
}

/**
 * Update a member's role. Only the OWNER can change roles.
 */
export async function updateMemberRole(
  tripId: string,
  userId: string,
  payload: UpdateTripMemberPayload,
): Promise<TripMemberResponse> {
  const response = await api
    .patch(`trips/${tripId}/members/${userId}`, { json: payload })
    .json<ApiDataResponse<TripMemberResponse>>();

  return response.data;
}

/**
 * Remove a member from the trip, or leave the trip (when removing yourself).
 */
export async function removeMember(tripId: string, userId: string): Promise<string> {
  const response = await api
    .delete(`trips/${tripId}/members/${userId}`)
    .json<ApiDataResponse<MessageResponse>>();

  return response.data.message;
}

// ===========================================================================
// TRIP DAYS
// ===========================================================================

/**
 * Create a new trip day. Date must be within the trip's date range.
 * Requires MEMBER or higher role.
 */
export async function createTripDay(
  tripId: string,
  payload: CreateTripDayPayload,
): Promise<TripDayResponse> {
  const response = await api
    .post(`trips/${tripId}/days`, { json: payload })
    .json<ApiDataResponse<TripDayResponse>>();

  return response.data;
}

/**
 * Update a trip day's date, title, or notes.
 * Requires MEMBER or higher role.
 */
export async function updateTripDay(
  tripId: string,
  dayId: string,
  payload: UpdateTripDayPayload,
): Promise<TripDayResponse> {
  const response = await api
    .patch(`trips/${tripId}/days/${dayId}`, { json: payload })
    .json<ApiDataResponse<TripDayResponse>>();

  return response.data;
}

/**
 * Delete a trip day and all its travel points.
 * Requires ADMIN or OWNER role.
 */
export async function deleteTripDay(tripId: string, dayId: string): Promise<string> {
  const response = await api
    .delete(`trips/${tripId}/days/${dayId}`)
    .json<ApiDataResponse<MessageResponse>>();

  return response.data.message;
}

/**
 * Reorder trip days according to the provided array of IDs.
 * Requires MEMBER or higher role.
 */
export async function reorderTripDays(
  tripId: string,
  payload: ReorderItemsPayload,
): Promise<TripDayResponse[]> {
  const response = await api
    .put(`trips/${tripId}/days/reorder`, { json: payload })
    .json<ApiDataResponse<TripDayResponse[]>>();

  return response.data;
}

// ===========================================================================
// TRAVEL POINTS
// ===========================================================================

/**
 * Create a travel point (place) within a trip day.
 * Requires MEMBER or higher role.
 */
/** The trip Home shows above the feed; null when there is neither an ongoing nor an upcoming one. */
export async function getCurrentTrip(): Promise<CurrentTrip | null> {
  const response = await api.get('trips/current').json<ApiDataResponse<CurrentTrip | null>>();
  return response.data;
}

/** A null day adds the point unscheduled. */
export async function createTravelPoint(
  tripId: string,
  dayId: string | null,
  payload: CreateTravelPointPayload,
): Promise<TravelPointResponse> {
  const response = await api
    .post(dayId ? `trips/${tripId}/days/${dayId}/points` : `trips/${tripId}/points`, {
      json: payload,
    })
    .json<ApiDataResponse<TravelPointResponse>>();

  return response.data;
}

/**
 * Update a travel point's details.
 * Requires MEMBER or higher role.
 */
export async function updateTravelPoint(
  tripId: string,
  dayId: string,
  pointId: string,
  payload: UpdateTravelPointPayload,
): Promise<TravelPointResponse> {
  const response = await api
    .patch(`trips/${tripId}/days/${dayId}/points/${pointId}`, {
      json: payload,
    })
    .json<ApiDataResponse<TravelPointResponse>>();

  return response.data;
}

/**
 * Delete a travel point.
 * Requires MEMBER or higher role.
 */
export async function deleteTravelPoint(
  tripId: string,
  dayId: string,
  pointId: string,
): Promise<string> {
  const response = await api
    .delete(`trips/${tripId}/days/${dayId}/points/${pointId}`)
    .json<ApiDataResponse<MessageResponse>>();

  return response.data.message;
}

/**
 * Reorder travel points within a day.
 * Requires MEMBER or higher role.
 */
export async function reorderTravelPoints(
  tripId: string,
  dayId: string,
  payload: ReorderItemsPayload,
): Promise<TravelPointResponse[]> {
  const response = await api
    .put(`trips/${tripId}/days/${dayId}/points/reorder`, { json: payload })
    .json<ApiDataResponse<TravelPointResponse[]>>();

  return response.data;
}

/**
 * Assign an unscheduled travel point to a specific trip day.
 * Requires MEMBER or higher role.
 */
export async function assignPointToDay(
  tripId: string,
  pointId: string,
  dayId: string,
): Promise<TravelPointResponse> {
  const response = await api
    .patch(`trips/${tripId}/points/${pointId}/assign`, { json: { dayId } })
    .json<ApiDataResponse<TravelPointResponse>>();

  return response.data;
}

/**
 * Delete any travel point belonging to a trip (scheduled or unscheduled).
 * Requires MEMBER or higher role.
 */
export async function deletePointFromTrip(tripId: string, pointId: string): Promise<string> {
  const response = await api
    .delete(`trips/${tripId}/points/${pointId}`)
    .json<ApiDataResponse<MessageResponse>>();

  return response.data.message;
}

/**
 * Move a scheduled travel point back to the unscheduled pool (sets tripDayId = null).
 * Requires MEMBER or higher role.
 */
export async function unschedulePoint(
  tripId: string,
  pointId: string,
): Promise<TravelPointResponse> {
  const response = await api
    .patch(`trips/${tripId}/points/${pointId}/unschedule`)
    .json<ApiDataResponse<TravelPointResponse>>();

  return response.data;
}

// ===========================================================================
// CHECKLIST
// ===========================================================================

/** Payload shape for creating a checklist item */
export interface CreateChecklistItemPayload {
  /** Chosen by the client so a replayed create collides instead of doubling. */
  id?: string;
  /** Which list to file it under. Omitted, the server uses the trip's first. */
  listId?: string;
  text: string;
  isChecked?: boolean;
  assignedToId?: string;
  category?: string;
}

/** Payload shape for updating a checklist item */
export interface UpdateChecklistItemPayload {
  text?: string;
  isChecked?: boolean;
  assignedToId?: string | null;
  category?: string;
}

/**
 * Create a checklist item for a trip.
 * Requires MEMBER or higher role.
 */
/** A named checklist within a trip, with the items filed under it. */
export interface ChecklistListResponse {
  id: string;
  tripId: string;
  name: string;
  orderIndex: number;
  /** Who made it, and so who decides who it is for. Null for older lists. */
  createdById: string | null;
  /**
   * Who the list is for, or null when it is the whole trip's — which is the
   * default. Null rather than an array of every member: the two look the same
   * today and mean different things the moment somebody joins.
   */
  memberIds: string[] | null;
  items: ChecklistItemResponse[];
  createdAt: string;
  updatedAt: string;
}

/** Null means everybody; so does naming the whole trip. */
export async function setChecklistListMembers(
  tripId: string,
  listId: string,
  userIds: string[] | null,
): Promise<ChecklistListResponse> {
  const response = await api
    .put(`trips/${tripId}/checklists/${listId}/members`, { json: { userIds } })
    .json<ApiDataResponse<ChecklistListResponse>>();

  return response.data;
}

export async function getChecklistLists(tripId: string): Promise<ChecklistListResponse[]> {
  const response = await api
    .get(`trips/${tripId}/checklists`)
    .json<ApiDataResponse<ChecklistListResponse[]>>();

  return response.data;
}

export async function createChecklistList(
  tripId: string,
  payload: { name: string },
): Promise<ChecklistListResponse> {
  const response = await api
    .post(`trips/${tripId}/checklists`, { json: payload })
    .json<ApiDataResponse<ChecklistListResponse>>();

  return response.data;
}

export async function updateChecklistList(
  tripId: string,
  listId: string,
  payload: { name?: string },
): Promise<ChecklistListResponse> {
  const response = await api
    .patch(`trips/${tripId}/checklists/${listId}`, { json: payload })
    .json<ApiDataResponse<ChecklistListResponse>>();

  return response.data;
}

export async function deleteChecklistList(tripId: string, listId: string): Promise<string> {
  const response = await api
    .delete(`trips/${tripId}/checklists/${listId}`)
    .json<ApiDataResponse<MessageResponse>>();

  return response.data.message;
}

export async function createChecklistItem(
  tripId: string,
  payload: CreateChecklistItemPayload,
): Promise<ChecklistItemResponse> {
  const response = await api
    .post(`trips/${tripId}/checklist`, { json: payload })
    .json<ApiDataResponse<ChecklistItemResponse>>();

  return response.data;
}

/**
 * Update a checklist item (text, checked state, assignment, category).
 * Requires MEMBER or higher role.
 */
export async function updateChecklistItem(
  tripId: string,
  itemId: string,
  payload: UpdateChecklistItemPayload,
): Promise<ChecklistItemResponse> {
  const response = await api
    .patch(`trips/${tripId}/checklist/${itemId}`, { json: payload })
    .json<ApiDataResponse<ChecklistItemResponse>>();

  return response.data;
}

/**
 * Delete a checklist item.
 * Requires MEMBER or higher role.
 */
export async function deleteChecklistItem(tripId: string, itemId: string): Promise<string> {
  const response = await api
    .delete(`trips/${tripId}/checklist/${itemId}`)
    .json<ApiDataResponse<MessageResponse>>();

  return response.data.message;
}

/**
 * Reorder checklist items.
 * Requires MEMBER or higher role.
 */
export async function reorderChecklistItems(
  tripId: string,
  payload: ReorderItemsPayload,
): Promise<ChecklistItemResponse[]> {
  const response = await api
    .put(`trips/${tripId}/checklist/reorder`, { json: payload })
    .json<ApiDataResponse<ChecklistItemResponse[]>>();

  return response.data;
}

// ---------------------------------------------------------------------------
// Named export object for convenient access
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Lodgings and important info
// ---------------------------------------------------------------------------

export async function createLodging(tripId: string, payload: LodgingPayload) {
  const response = await api
    .post(`trips/${tripId}/lodgings`, { json: payload })
    .json<ApiDataResponse<LodgingResponse>>();
  return response.data;
}

export async function updateLodging(
  tripId: string,
  lodgingId: string,
  payload: Partial<LodgingPayload>,
) {
  const response = await api
    .patch(`trips/${tripId}/lodgings/${lodgingId}`, { json: payload })
    .json<ApiDataResponse<LodgingResponse>>();
  return response.data;
}

export async function deleteLodging(tripId: string, lodgingId: string): Promise<string> {
  const response = await api
    .delete(`trips/${tripId}/lodgings/${lodgingId}`)
    .json<ApiDataResponse<MessageResponse>>();
  return response.data.message;
}

export async function createTripNote(tripId: string, payload: TripNotePayload) {
  const response = await api
    .post(`trips/${tripId}/notes`, { json: payload })
    .json<ApiDataResponse<TripNoteResponse>>();
  return response.data;
}

export async function updateTripNote(
  tripId: string,
  noteId: string,
  payload: Partial<TripNotePayload>,
) {
  const response = await api
    .patch(`trips/${tripId}/notes/${noteId}`, { json: payload })
    .json<ApiDataResponse<TripNoteResponse>>();
  return response.data;
}

export async function deleteTripNote(tripId: string, noteId: string): Promise<string> {
  const response = await api
    .delete(`trips/${tripId}/notes/${noteId}`)
    .json<ApiDataResponse<MessageResponse>>();
  return response.data.message;
}

export const tripsApi = {
  // Trips
  createTrip,
  getTrips,
  getTripById,
  updateTrip,
  deleteTrip,

  // Members
  inviteMember,
  updateMemberRole,
  removeMember,

  // Invitations
  getTripInvitation,
  getTripInvitations,
  acceptTripInvitation,
  declineTripInvitation,

  // Days
  createTripDay,
  updateTripDay,
  deleteTripDay,
  reorderTripDays,

  // Travel Points
  createTravelPoint,
  updateTravelPoint,
  deleteTravelPoint,
  reorderTravelPoints,
  assignPointToDay,
  unschedulePoint,
  deletePointFromTrip,

  // Checklist
  getChecklistLists,
  createChecklistList,
  updateChecklistList,
  setChecklistListMembers,
  deleteChecklistList,
  createChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  reorderChecklistItems,
  // Lodgings and important info
  createLodging,
  updateLodging,
  deleteLodging,
  createTripNote,
  updateTripNote,
  deleteTripNote,
} as const;

export default tripsApi;
